import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ChevronDown } from "lucide-react";

import { cn } from "../../lib/utils.js";
import { SelectContent, SelectItem, SelectTrigger } from "./select.js";
import { SpecularButton } from "./specular-button.js";

/**
 * Drop-in replacement for a native <select>: same `<option>` children, same
 * `value`, and `onChange` still receives an event whose `target.value` is the
 * chosen option's value (as a string, like the DOM). Renders the app's
 * dropdown -- the shadcn Select with the gliding highlight, check-mark pop,
 * keyboard support and typeahead -- in a portal, so it is never clipped.
 *
 * Native behaviours kept: an option without `value` uses its text; a value
 * that matches no option shows the first option's label; `disabled` options;
 * `name` / `required` still take part in <form> submission.
 */
export interface SelectFieldProps {
  value?: string | number | null;
  /** Same shape as a native select's onChange: read `e.target.value`. */
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  /** Alternative to onChange for new code. */
  onValueChange?: (value: string) => void;
  /** <option> elements (arrays, fragments and conditionals are fine). */
  children?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  name?: string;
  id?: string;
  title?: string;
  "aria-label"?: string;
  /** Trigger height / text size. */
  size?: "sm" | "md";
  /**
   * Toolbar / filter-bar trigger: a SpecularButton (border highlight follows
   * the pointer). Each holds a WebGL context -- keep to a few per screen and
   * never use it inside forms or table rows.
   */
  specular?: boolean;
  /** Classes for the trigger (width, margins). */
  className?: string;
  /** Classes for the floating list. */
  contentClassName?: string;
}

interface ParsedOption {
  value: string;
  label: React.ReactNode;
  text: string;
  disabled: boolean;
}

// Radix reserves "" for "no selection", so an <option value=""> maps to this.
const EMPTY = "__select_field_empty__";

function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children);
  return "";
}

function parseOptions(children: React.ReactNode, out: ParsedOption[] = []): ParsedOption[] {
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return;
    if (child.type === React.Fragment) {
      parseOptions((child.props as { children?: React.ReactNode }).children, out);
      return;
    }
    if (child.type !== "option") return;
    const p = child.props as React.OptionHTMLAttributes<HTMLOptionElement>;
    const text = textOf(p.children);
    out.push({
      value: p.value != null ? String(p.value) : text,
      label: p.children,
      text,
      disabled: !!p.disabled,
    });
  });
  return out;
}

// Drops undefined keys (exactOptionalPropertyTypes): only set attributes are passed on.
function defined<T extends Record<string, unknown>>(obj: T): { [K in keyof T]?: Exclude<T[K], undefined> } {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as { [K in keyof T]?: Exclude<T[K], undefined> };
}

const toRadix = (v: string) => (v === "" ? EMPTY : v);
const fromRadix = (v: string) => (v === EMPTY ? "" : v);

const SPECULAR_TRIGGER = "group h-9 justify-between gap-1.5 px-2.5 font-normal text-surface-900 [&>span]:min-w-0";
const SIZES = { sm: "h-8 px-2.5 text-[13px]", md: "" } as const;

export function SelectField({
  value, onChange, onValueChange, children, disabled, required, name, id, title,
  "aria-label": ariaLabel, size = "md", specular = false, className, contentClassName,
}: SelectFieldProps) {
  const options = React.useMemo(() => parseOptions(children), [children]);

  // Uncontrolled (no `value`): like the DOM, start on the first option.
  const [inner, setInner] = React.useState(() => options[0]?.value ?? "");
  const current = value === undefined ? inner : String(value ?? "");
  const selected = options.find((o) => o.value === current);
  // A native select shows its first option when the value matches none.
  const shown = selected ?? options[0];

  const handleChange = (radixValue: string) => {
    const v = fromRadix(radixValue);
    if (value === undefined) setInner(v);
    onValueChange?.(v);
    if (onChange) {
      const target = { value: v, name: name ?? "" };
      onChange({ target, currentTarget: target } as unknown as React.ChangeEvent<HTMLSelectElement>);
    }
  };

  const triggerAttrs = defined({ id, title, "aria-label": ariaLabel });

  // Radix renders `placeholder` when nothing matches and the children when
  // something does -- both show the native-equivalent label.
  const valueNode = <SelectPrimitive.Value placeholder={shown?.label}>{shown?.label}</SelectPrimitive.Value>;

  return (
    <SelectPrimitive.Root
      value={selected ? toRadix(selected.value) : ""}
      onValueChange={handleChange}
      {...defined({ disabled })}
    >
      {specular ? (
        <SelectPrimitive.Trigger asChild>
          <SpecularButton size="lg" {...triggerAttrs} className={cn(SPECULAR_TRIGGER, className)}>
            {valueNode}
            <ChevronDown size={14} className="shrink-0 transition-transform duration-200 group-data-[state=open]:rotate-180" />
          </SpecularButton>
        </SelectPrimitive.Trigger>
      ) : (
        <SelectTrigger {...triggerAttrs} className={cn("text-left", SIZES[size], className)}>
          {valueNode}
        </SelectTrigger>
      )}
      <SelectContent className={contentClassName}>
        {options.map((o, i) => (
          <SelectItem key={`${i}:${o.value}`} value={toRadix(o.value)} disabled={o.disabled} textValue={o.text}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
      {/* Form participation: a hidden native select holding the real value (""
          for the empty option, which Radix would submit as its internal
          placeholder), so FormData and `required` validation behave natively. */}
      {(name !== undefined || required) && (
        <select
          aria-hidden="true"
          tabIndex={-1}
          // top-0/left-0: an absolute input left at its static position far down a
          // long page stretches the document and scrolls the whole window.
          className="sr-only top-0 left-0"
          value={selected?.value ?? ""}
          onChange={() => {}}
          {...defined({ name, required, disabled })}
        >
          {!selected && <option value="" />}
          {options.map((o, i) => <option key={`${i}:${o.value}`} value={o.value}>{o.text}</option>)}
        </select>
      )}
    </SelectPrimitive.Root>
  );
}
